import { Suspense, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import { usePlayer } from '@/lib/PlayerContext';
import { formatDuration, formatDurationTimestamp, formatRelativeDate } from '@/utils/format';
import { QueryErrorBoundary } from '@/components/QueryErrorBoundary';
import type { HistoryPageQuery } from '@/__generated__/HistoryPageQuery.graphql';
import type { HistoryPage_history$key } from '@/__generated__/HistoryPage_history.graphql';

/**
 * Root query for HistoryPage - fetches listening history for the viewer
 */
const historyPageQuery = graphql`
  query HistoryPageQuery {
    ...HistoryPage_history
  }
`;

/**
 * Fragment for paginated listening history
 */
const HistoryPage_history = graphql`
  fragment HistoryPage_history on QueryRoot
  @refetchable(queryName: "HistoryPageRefetchQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 20 }
    after: { type: "String" }
  ) {
    listeningHistory(first: $first, after: $after)
      @connection(key: "HistoryPage_listeningHistory") {
      edges {
        node {
          id
          progressSeconds
          durationSeconds
          completed
          listenedAt
          episode {
            id
            topicSlug
            episodeId
            title
            summary
            audioS3Key
            durationSeconds
            publishedAt
            chapters {
              id
              title
              startTimeMs
              endTimeMs
              description
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

interface HistoryEntryData {
  readonly id: string;
  readonly progressSeconds: number;
  readonly durationSeconds: number;
  readonly completed: boolean;
  readonly listenedAt: string;
  readonly episode: {
    readonly id: string;
    readonly topicSlug: string;
    readonly episodeId: string;
    readonly title: string;
    readonly summary: string;
    readonly audioS3Key: string;
    readonly durationSeconds: number;
    readonly publishedAt: string;
    readonly chapters: ReadonlyArray<{
      readonly id: string;
      readonly title: string;
      readonly startTimeMs: number;
      readonly endTimeMs: number;
      readonly description: string | null | undefined;
    }>;
  };
}

interface HistoryItemProps {
  entry: HistoryEntryData;
  onResume: () => void;
  isCurrentEpisode: boolean;
  isPlaying: boolean;
}

function HistoryItem({ entry, onResume, isCurrentEpisode, isPlaying }: HistoryItemProps) {
  const progressPercent = Math.round((entry.progressSeconds / entry.durationSeconds) * 100);

  return (
    <div
      className={`rounded-xl border bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-4 transition-all hover:border-gray-700 ${
        isCurrentEpisode ? 'border-indigo-500 ring-1 ring-indigo-500/50' : 'border-gray-800'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Resume/Play button */}
        <button
          type="button"
          onClick={onResume}
          className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
            isPlaying
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          aria-label={isPlaying ? 'Pause' : entry.completed ? 'Play again' : 'Resume'}
        >
          {isPlaying ? (
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg className="h-5 w-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Entry info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <Link
              to={`/p/${entry.episode.topicSlug}`}
              className="font-medium text-indigo-400 hover:text-indigo-300"
            >
              {entry.episode.topicSlug}
            </Link>
            <span>•</span>
            <span>{formatRelativeDate(entry.listenedAt)}</span>
            {entry.completed && (
              <>
                <span>•</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-green-400">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                  </svg>
                  Completed
                </span>
              </>
            )}
          </div>

          <h3 className="font-semibold text-white line-clamp-2 mb-1">
            {entry.episode.title}
          </h3>

          <p className="text-sm text-gray-400 line-clamp-2 mb-3">
            {entry.episode.summary}
          </p>

          {/* Progress bar */}
          <div className="mb-2">
            <div className="h-1.5 w-full rounded-full bg-gray-700">
              <div
                className={`h-1.5 rounded-full ${
                  entry.completed ? 'bg-green-500' : 'bg-indigo-500'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
              <span>
                {entry.completed
                  ? 'Completed'
                  : `${formatDurationTimestamp(entry.progressSeconds)} / ${formatDurationTimestamp(entry.durationSeconds)}`}
              </span>
              <span>{formatDuration(entry.durationSeconds)} total</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-800">
        <svg
          className="h-8 w-8 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">No listening history</h3>
      <p className="text-gray-400 mb-6 max-w-md mx-auto">
        Your listening history will appear here as you listen to episodes. Start exploring and listening to build your history.
      </p>
      <Link
        to="/dashboard/discover"
        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-500"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>
        Discover Topics
      </Link>
    </div>
  );
}

interface HistoryPageContentProps {
  queryRef: HistoryPage_history$key;
}

function HistoryPageContent({ queryRef }: HistoryPageContentProps) {
  const { playEpisode, currentEpisode, isPlaying, setIsPlaying } = usePlayer();

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    HistoryPage_history,
    queryRef
  );

  const entries = useMemo(() => {
    return data.listeningHistory.edges.map((edge) => edge.node);
  }, [data.listeningHistory.edges]);

  const handleResume = useCallback((entry: HistoryEntryData) => {
    const isCurrentEpisode = currentEpisode?.episodeId === entry.episode.episodeId;
    if (isCurrentEpisode) {
      setIsPlaying(!isPlaying);
    } else {
      // Note: startAt functionality will be added in Task 27 (PlayerContext update)
      playEpisode({
        episodeId: entry.episode.episodeId,
        topicSlug: entry.episode.topicSlug,
        topicName: entry.episode.topicSlug,
        title: entry.episode.title,
        summary: entry.episode.summary,
        audioS3Key: entry.episode.audioS3Key,
        durationSeconds: entry.episode.durationSeconds,
        publishedAt: entry.episode.publishedAt,
        chapters: entry.episode.chapters.map((ch) => ({
          id: ch.id,
          title: ch.title,
          startTimeMs: ch.startTimeMs,
          endTimeMs: ch.endTimeMs,
        })),
      });
    }
  }, [currentEpisode, isPlaying, playEpisode, setIsPlaying]);

  if (entries.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">History</h2>
        <p className="mt-1 text-gray-400">
          Your listening history ({data.listeningHistory.totalCount})
        </p>
      </div>

      <div className="space-y-4">
        {entries.map((entry) => (
          <HistoryItem
            key={entry.id}
            entry={entry}
            onResume={() => handleResume(entry)}
            isCurrentEpisode={currentEpisode?.episodeId === entry.episode.episodeId}
            isPlaying={currentEpisode?.episodeId === entry.episode.episodeId && isPlaying}
          />
        ))}
      </div>

      {hasNext && (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => loadNext(20)}
            disabled={isLoadingNext}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingNext ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}
    </>
  );
}

function HistoryPageLoader() {
  const data = useLazyLoadQuery<HistoryPageQuery>(historyPageQuery, {}, {
    fetchPolicy: 'store-and-network',
  });

  return <HistoryPageContent queryRef={data} />;
}

export function HistoryPage() {
  return (
    <QueryErrorBoundary>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          </div>
        }
      >
        <HistoryPageLoader />
      </Suspense>
    </QueryErrorBoundary>
  );
}
